import React, { useState, useRef, useEffect } from 'react';
import { generateRandomNumber } from './random';

// Static file paths from public/media/
const VIDEO_PATHS = {
  rock_scissors: "/media/rock_scissors.mp4",
  scissors_cut_paper: "/media/scissors_cut_paper.mp4",
  paper_covers_rock: "/media/paper_covers_rock.mp4",
  rock_tie: "/media/rock_tie.mp4",
  paper_tie: "/media/paper_tie.mp4",
  scissors_tie: "/media/scissors_tie.mp4"
};

const WINNING_VIDEOS = {
  Rock: { Scissors: VIDEO_PATHS.rock_scissors },
  Paper: { Rock: VIDEO_PATHS.paper_covers_rock },
  Scissors: { Paper: VIDEO_PATHS.scissors_cut_paper }
};

const LOSING_VIDEOS = {
  Rock: { Paper: VIDEO_PATHS.paper_covers_rock },
  Paper: { Scissors: VIDEO_PATHS.scissors_cut_paper },
  Scissors: { Rock: VIDEO_PATHS.rock_scissors }
};

const TIE_VIDEOS = {
  Rock: VIDEO_PATHS.rock_tie,
  Paper: VIDEO_PATHS.paper_tie,
  Scissors: VIDEO_PATHS.scissors_tie
};

// Fullscreen request helper
function requestFullScreen(element) {
  if (element.requestFullscreen) element.requestFullscreen();
  else if (element.webkitRequestFullscreen) element.webkitRequestFullscreen();
  else if (element.mozRequestFullScreen) element.mozRequestFullScreen();
  else if (element.msRequestFullscreen) element.msRequestFullscreen();
}

const RockPaperScissors = () => {
  const [playerScore, setPlayerScore] = useState(0);
  const [computerScore, setComputerScore] = useState(0);
  const [outcome, setOutcome] = useState('');
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const videoRef = useRef(null);

  // Lazy-load background image
  useEffect(() => {
    const bg = new Image();
    bg.src = "/media/RPS_background.jpeg";
    bg.onload = () => {
      const container = document.querySelector(".RPS_container");
      if (container) container.classList.add("loaded-bg");
    };
  }, []);

  const determineOutcome = (playerMove, computerMove) => {
    if (playerMove === computerMove) {
      setOutcome("Tie!");
      return "tie";
    }

    const winCases = {
      Rock: "Scissors",
      Paper: "Rock",
      Scissors: "Paper"
    };

    if (winCases[playerMove] === computerMove) {
      setPlayerScore(prev => prev + 1);
      setOutcome("You win!");
      return "win";
    }

    setComputerScore(prev => prev + 1);
    setOutcome("Computer wins!");
    return "lose";
  };

  const playVideo = (playerMove, computerMove, result) => {
    let videoSrc;

    if (result === "win") videoSrc = WINNING_VIDEOS[playerMove][computerMove];
    else if (result === "lose") videoSrc = LOSING_VIDEOS[playerMove][computerMove];
    else videoSrc = TIE_VIDEOS[playerMove];

    const video = videoRef.current;
    video.src = videoSrc;
    video.style.display = "block";
    video.controls = false;

    video.play();
    requestFullScreen(video);
  };

  const handlePlayerMove = (move) => {
    if (isVideoPlaying) return;

    const randomVal = generateRandomNumber(1, 3);
    const computerMove = ["Rock", "Paper", "Scissors"][randomVal - 1];

    const result = determineOutcome(move, computerMove);
    playVideo(move, computerMove, result);
  };

  // Video event listeners including automatic fullscreen exit
  useEffect(() => {
    const video = videoRef.current;

    const onPlay = () => setIsVideoPlaying(true);

    const onEnd = () => {
      setIsVideoPlaying(false);

      // Auto-exit fullscreen
      if (document.fullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
      }

      // Hide video
      video.style.display = "none";
    };

    video.addEventListener("play", onPlay);
    video.addEventListener("ended", onEnd);

    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("ended", onEnd);
    };
  }, []);

  return (
    <>
      <div className="RPS_container">
        {!isVideoPlaying && (
          <div className="RPS_header">
            <p className="Player_score">Player Score: {playerScore}</p>

            <button onClick={() => handlePlayerMove('Rock')} className="rock_button">
              Rock
            </button>
            <button onClick={() => handlePlayerMove('Paper')} className="paper_button">
              Paper
            </button>
            <button onClick={() => handlePlayerMove('Scissors')} className="scissors_button">
              Scissors
            </button>

            <p className="Computer_score">Computer Score: {computerScore}</p>
          </div>
        )}

        <p>{outcome}</p>
      </div>

      {/* Fullscreen video element */}
      <video
        ref={videoRef}
        style={{ display: "none" }}
        preload="metadata"
      />
    </>
  );
};

export default RockPaperScissors;
